import React, { useEffect, useState, useCallback } from "react";
import {
    Button,
    Typography,
    Row,
    Col,
    Card,
    Space,
    message,
    Empty,
    Modal,
    Pagination,
} from "antd";
import {
    ArrowLeftOutlined,
    EditOutlined,
    DeleteOutlined,
    PictureOutlined, EyeOutlined
} from "@ant-design/icons";
import FAQsApi from "../../services/faqs";
import { useNavigate, useLocation } from "react-router-dom";
import './faq.css'

const { Title, Text } = Typography;

const FAQManagement: React.FC = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const [faqs, setFaqs] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [deleteModal, setDeleteModal] = useState<{ open: boolean; id?: string }>({ open: false });
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(5);
    const navigate = useNavigate();
    const location = useLocation();

    const fetchFaqs = useCallback(async () => {
        setLoading(true);
        try {
            const response = await FAQsApi.getFaqsList();
            const faqList =
                Array.isArray(response)
                    ? response
                    : Array.isArray(response?.data)
                        ? response.data
                        : Array.isArray(response?.items)
                            ? response.items
                            : [];

            setFaqs(faqList);
        } catch (err) {
            console.error("Error fetching FAQs:", err);
            messageApi.error("Failed to fetch FAQs.");
        } finally {
            setLoading(false);
        }
    }, [messageApi]);

    useEffect(() => {
        fetchFaqs();
    }, [fetchFaqs]);

    const handleDelete = async (id: string) => {
        try {
            await FAQsApi.deleteFaq(id);
            messageApi.success("FAQ deleted successfully!");
            fetchFaqs();
        } catch (err) {
            messageApi.error("Failed to delete FAQ");
        } finally {
            setDeleteModal({ open: false });
        }
    };

    const startIndex = (currentPage - 1) * pageSize;
    const paginatedFaqs = faqs.slice(startIndex, startIndex + pageSize);

    return (
        <div
            style={{
                width: "100%",
                background: "#fff",
                minHeight: "100vh",
                padding: "16px",
            }}
        >
            {contextHolder}

            {/* Delete confirmation modal */}
            <Modal
                title={
                    <div style={{ whiteSpace: "normal", wordWrap: "break-word", paddingRight: "32px" }}>
                        Are you sure you want to delete this FAQ? This action cannot be undone.
                    </div>
                }
                open={deleteModal.open}
                onOk={() => deleteModal.id && handleDelete(deleteModal.id)}
                onCancel={() => setDeleteModal({ open: false })}
                okText="Yes"
                cancelText="No"
                centered
                okButtonProps={{
                    style: {
                        backgroundColor: "#3E767A",
                        borderColor: "#004D45",
                        color: "#fff",
                        borderRadius: 6,
                    },
                }}
            />


            {/* Header */}
            <Row align="middle" style={{ marginBottom: 24 }}>
                <Col flex="none">
                    <ArrowLeftOutlined
                        style={{
                            fontSize: 20,
                            color: "#595959",
                            marginRight: 12,
                            cursor: "pointer",
                        }}
                        onClick={() => window.history.back()}
                    />
                </Col>
                <Col flex="auto">
                    <Title
                        level={4}
                        style={{ margin: 0, color: "#004D45", fontWeight: 600 }}
                    >
                        Existing FAQ
                    </Title>
                </Col>
            </Row>

            {/* Loader / Empty / List */}
            {loading ? (
                <Row justify="center" style={{ padding: "80px 0" }}>
                    <img
                        src="/images/newLoading.gif" // <-- path to your GIF
                        alt="Loading..."
                        style={{ width: 200, height: 150 }}
                    />
                </Row>
            ) : faqs.length === 0 ? (
                <Empty description="No FAQs found" style={{ marginTop: 80 }} />
            ) : (
                <>
                    <Space direction="vertical" size="large" style={{ width: "100%" }}>
                        {paginatedFaqs.map((faq) => {
                            const image =
                                faq.faqImagesResponses?.length > 0
                                    ? faq.faqImagesResponses[0].fileUrl
                                    : "";

                            return (
                                <Card
                                    key={faq.id}
                                    style={{
                                        width: "100%",
                                        borderRadius: "16px",
                                        background: "#fff",
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
                                        border: "none",
                                    }}
                                    bodyStyle={{
                                        padding: "16px 20px",
                                    }}
                                >
                                    <Row gutter={[16, 12]} align="middle">
                                        {/* Image */}
                                        <Col xs={24} sm={4} md={3} lg={2}>
                                            <div
                                                style={{
                                                    width: "70px",
                                                    height: "70px",
                                                    borderRadius: "12px",
                                                    overflow: "hidden",
                                                    background: "#E8F0EF",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    margin: "0 auto",
                                                }}
                                            >
                                                {image ? (
                                                    <img
                                                        src={image}
                                                        alt={faq.question}
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={() => navigate("/CreateFAQ/view", { state: { faq, mode: "view" } })}
                                                    />
                                                ) : (
                                                    <PictureOutlined
                                                        style={{ fontSize: 28, color: "#9FB5B3", cursor: "pointer" }}
                                                        onClick={() => navigate("/CreateFAQ/view", { state: { faq, mode: "view" } })}
                                                    />
                                                )}

                                            </div>
                                        </Col>

                                        {/* Text + Actions */}
                                        <Col xs={24} sm={20} md={21} lg={22}>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "flex-start",
                                                    flexWrap: "wrap",
                                                    gap: "8px",
                                                }}
                                            >
                                                {/* Text section */}
                                                <div style={{ flex: 1, minWidth: "240px" }}>
                                                    <Title
                                                        level={5}
                                                        style={{
                                                            margin: 0,
                                                            color: "#004D45",
                                                            fontWeight: 600,
                                                            fontSize: "16px",
                                                            lineHeight: 1.4,
                                                        }}
                                                    >
                                                        {faq.question}
                                                    </Title>

                                                    <Text
                                                        style={{
                                                            color: "#7A8B89",
                                                            fontSize: "14px",
                                                            lineHeight: 1.5,
                                                            marginTop: "4px",
                                                            display: "block",
                                                        }}
                                                        ellipsis={{ tooltip: true }}
                                                    >
                                                        {(() => {
                                                            const cleanAnswer = faq.answer
                                                                ?.replace(/<[^>]+>/g, "")      // remove HTML tags
                                                                .replace(/&[^;\s]+;/g, " ")    // remove HTML entities (&nbsp;, &amp;, etc.)
                                                                .replace(/\s+/g, " ")          // normalize spaces
                                                                .trim();                       // trim whitespace

                                                            return cleanAnswer && cleanAnswer.length > 0
                                                                ? cleanAnswer.slice(0, 90) + (cleanAnswer.length > 90 ? "..." : "")
                                                                : "No answer";
                                                        })()}

                                                    </Text>
                                                </div>

                                                {/* Action buttons */}
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        gap: "10px",
                                                        flexWrap: "wrap",
                                                        justifyContent: "flex-end",
                                                    }}
                                                >

                                                    <Button
                                                        icon={<EyeOutlined />}
                                                        onClick={() => navigate("/CreateFAQ/view", { state: { faq, mode: "view" } })}
                                                        style={{
                                                            borderRadius: "24px",
                                                            border: "1px solid #BFD6D2",
                                                            background: "#F6FAF9",
                                                            color: "#004D45",
                                                            fontWeight: 500,
                                                            padding: "0 16px",
                                                        }}
                                                    >
                                                        View
                                                    </Button>
                                                    <Button
                                                        icon={<EditOutlined />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate("/CreateFAQ/edit", { state: { faq, mode: "edit" } });
                                                        }}
                                                        style={{
                                                            borderRadius: "24px",
                                                            border: "1px solid #BFD6D2",
                                                            background: "#F6FAF9",
                                                            color: "#004D45",
                                                            fontWeight: 500,
                                                            padding: "0 16px",
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>

                                                    <Button
                                                        icon={<DeleteOutlined />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDeleteModal({ open: true, id: faq.id });
                                                        }}
                                                        style={{
                                                            borderRadius: "24px",
                                                            border: "1px solid #BFD6D2",
                                                            background: "#F6FAF9",
                                                            color: "#004D45",
                                                            fontWeight: 500,
                                                            padding: "0 16px",
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>
                            );
                        })}
                    </Space>

                    {/* Pagination */}
                    <Row justify="center" style={{ marginTop: 24 }}>
                        <Pagination
                            current={currentPage}
                            pageSize={pageSize}
                            total={faqs.length}
                            showSizeChanger
                            pageSizeOptions={["5", "10", "20"]}
                            onChange={(page, size) => {
                                setCurrentPage(page);
                                setPageSize(size || 5);
                            }}
                            showTotal={(total, range) =>
                                `${range[0]}-${range[1]} of ${total} FAQs`
                            }
                        />
                    </Row>
                </>
            )}
        </div>
    );
};

export default FAQManagement;
const styles: { [key: string]: React.CSSProperties } = {
    description: {
        maxWidth: "100%",         // keeps it responsive — 90% of container width
        width: "600px",          // fixed readable width on large screens
        margin: "0 auto",        // center align
        wordWrap: "break-word",  // wrap long words if needed
        overflowWrap: "break-word",
        whiteSpace: "normal",    // ensure lines wrap
        textAlign: "left",       // optional, can be "justify"
        lineHeight: "1.5",
        fontSize: "13px",
        color: "#999",
    },

};

